import { ContainerItem } from './ContainerItem';
import { ReceiptSubmitRow } from './ReceiptSubmitRow';

/**
 * UpdateReceivingLogRequest.ts
 *
 * Wire types for `PUT /V1/receiving-log/{logId}` — handoff-18 §3. The structured
 * edit of an already-submitted log: the caller sends ROW changes and the backend
 * re-derives `MM.ReceivingLog.Quantity` from what survives.
 *
 * FROZEN CONTRACT (handoff-18-frontend-log-edit.md, revision 2, 2026-08-06).
 *
 * Several fields you would expect to find here are DELIBERATELY ABSENT, and the
 * absences are the point — they are how the type system stops a save from doing
 * damage that a 200 response would happily report as success:
 *
 * - No `quantity` anywhere (§2/§3.5). It is derived server-side from the item
 *   rows; a hand-typed value is the string-contradicts-the-labels corruption
 *   this endpoint exists to prevent. Read it back off the response instead.
 * - No `poNumber` on {@link LogRowFieldEdits} (§3.5) — a mismatched one is a
 *   `400`, and a log's PO cannot be changed after submit by any endpoint.
 * - No `poNumber` on {@link ReceiptUpdatePatch} (§4.2) — a mismatch there is
 *   refused, so omitting it is the only correct behaviour.
 *
 *   ⚠️ §4.2 IS WRONG ABOUT `added`, and the doc has not been corrected upstream.
 *   It says the PO is "silently overwritten" there too, so omitting it is safe.
 *   It is not: `receipts.added` reuses the SUBMIT's `ReceivingLogReceipt` C# model,
 *   whose `PoNumber` is a non-nullable `string`, which ASP.NET's implicit-required
 *   handling turns into a hard validation error. Omitting it returns
 *   `400 Receipts.Added[0].PoNumber: The PoNumber field is required`. So an added
 *   row CARRIES the log's own PO — see {@link ReceiptAddRow}. Sending it is
 *   harmless even where the doc is right, because the backend stamps the log's PO
 *   over whatever arrives.
 * - No `itemId` on a container addition (§3.6) — the backend mints them on
 *   insert and overwrites anything sent, so an id minted here would not be the
 *   id in the table or in the QR on the box. Read `addedItems[]` off the
 *   response instead. This is deliberately unlike the submit path.
 * - No `pushNote`, and no other channel to the GP PO note (§7). That note is
 *   the record of what was received, written ONCE by the submit; no edit path
 *   writes it, by design, because `MM.ReceivingAddnote` appends rather than
 *   replaces and nothing can take a duplicate block back off a vendor-visible
 *   note. The backend removed the field from this request entirely — sending it
 *   is not "harmless but ignored", it is a field that no longer exists.
 */

/**
 * Field edits for the `MM.ReceivingLog` row itself. Every property is optional;
 * ONLY the ones present are written, so this is a sparse patch like
 * {@link ReceiptUpdatePatch}.
 *
 * ⚠️ This type is an ALLOW-LIST, not a `Partial<ApiReceivingLogRow>`. Four
 * fields of that row are missing on purpose and must not be added back:
 *
 * - `id` — ignored (the route's log id always wins).
 * - `quantity` — ignored; derived from the item rows (§2).
 * - `items` — the note text has exactly one channel, `UpdateReceivingLogRequest.items`.
 * - `poNumber` — **refused with a `400`** when it differs from the stored value (§3.5).
 *
 * `id`/`quantity` being merely ignored makes them look harmless to include, but
 * `logRowStatus` then comes back "No log-row fields to update" for what the user
 * experienced as a field edit (§6). `poNumber` fails the whole save outright.
 * Keeping all four off the type means an echoed-back GET row cannot compile.
 */
export interface LogRowFieldEdits {
  vendor?: string;
  jobNumber?: string;
  jobName?: string;
  jcDept?: string;
  email?: string;
  location?: string;
  /** `null` is not accepted: only non-null fields are written, so a date cannot be CLEARED through this endpoint (§3.1). */
  dateIn?: string;
  dateOut?: string;
  takenBy?: string;
  shopFloorSF?: number;
  shipmentType?: string;
  loggedBy?: string;
  checkedOut?: boolean;
  notes?: string;
  storageFacility?: string;
}

/** The four `logRow` keys that must never reach the wire — see {@link LogRowFieldEdits}. */
export const NON_EDITABLE_LOG_ROW_FIELDS: readonly string[] = ['id', 'quantity', 'items', 'poNumber'];

/**
 * A SPARSE PATCH of one existing receipt-ledger row (§3.1). `id` is required and
 * must belong to the log in the route; every other field is written only when
 * present, and an omitted field keeps its stored value.
 *
 * ⚠️ This is the single most dangerous object in the feature. Two fields do
 * real-world damage when round-tripped from the GET rather than genuinely edited:
 *
 * - `completesLine: false` on a row that was closed **RE-OPENS THE PO LINE**, so
 *   the PO screen reads short on a delivery that arrived complete.
 * - `isDamaged: false` on a damaged row **ERASES THAT FACT** while its damage
 *   photos stay attached to the row.
 *
 * And `damagedQuantity` is the same hazard through a second door: when you send
 * one, the backend DERIVES `isDamaged` from it (`> 0`), so `damagedQuantity: 0`
 * clears the damage flag even though you never mentioned the flag.
 *
 * You also **cannot clear a field back to `null`** here — only non-null fields
 * are written, so `"notes": null` leaves the stored note alone. Empty string is
 * the closest available clear.
 *
 * The way this codebase keeps all of that from happening by accident is that
 * nothing ever builds one of these from a `ReceivingLogEditReceipt`: the patch
 * is assembled from a per-row map of the fields the user actually touched, and
 * the builder that emits it (`buildReceiptUpdates`) is given that map ALONE —
 * the loaded rows are not in its scope, so it has nothing to echo.
 */
export interface ReceiptUpdatePatch {
  /** REQUIRED. An entry with no id is a `400` — use `added` for new rows. */
  id: number;

  /**
   * REQUIRED, and NOT an edit — the log's own PO, unchanged.
   *
   * `receipts.updated` deserializes into the same `ReceivingLogReceipt` model as
   * `added`, whose `PoNumber` is a non-nullable `string`, so ASP.NET refuses the
   * whole request without it: `400 Receipts.Updated[0].PoNumber: The PoNumber field
   * is required`. The API doc says a caller "may send a sparse row", and that holds
   * for every field except this one.
   *
   * Safe to send precisely because it is not a change: §4.2 refuses a MISMATCHED PO
   * on `updated`, and this is stamped from the log being edited, so it always equals
   * the stored value. It is an identity field that happens to live in the patch, not
   * a field the user can move — nothing on the screen writes it, and a log's PO
   * cannot be changed by any endpoint (§3.5).
   */
  poNumber: string;
  quantityReceived?: number;
  /** `"Complete"` or `"Partial"` — NEVER `"Missing"`; a shortfall is derived at read time, never stored. */
  receiptStatus?: 'Complete' | 'Partial';
  /** Send ONLY when the user changed it — `false` on a closed row re-opens the PO line. */
  completesLine?: boolean;
  notes?: string;
  /** Send ONLY when the user changed it — `false` on a damaged row erases the damage. */
  isDamaged?: boolean;
  /** Sending this REWRITES `isDamaged` (`> 0`), including clearing it at `0`. */
  damagedQuantity?: number;
  damageNote?: string;
  damageDisposition?: 'Refuse' | 'Return' | 'Keep';
  itemNumber?: string;
  itemDescription?: string;
  unitOfMeasure?: string;
  quantityOrdered?: number;
  poLineSequence?: number;
}

/**
 * Receipt-row changes, applied in the order deletes → updates → adds.
 *
 * `added` rows are the same shape the submit sends ({@link ReceiptSubmitRow}) —
 * no `id`, no `poNumber`; the backend stamps `receivingLogID` and the log's PO
 * onto every one. **The API validates nothing on them** (§3.2): no required
 * field, no minimum quantity, no allowed-value check on `receiptStatus`, and no
 * check of `poLineSequence` against the PO. A body of `{"quantityReceived": 2}`
 * inserts cleanly and lands in the ledger that drives `qtyReceived`/`isClosed`
 * on the PO screen, so `validateAddedReceiptRows` in
 * `receiving-log-save.rules.ts` is the only line of defence there is.
 */
/**
 * An added receipt row: the submit's own shape PLUS the log's PO number.
 *
 * `poNumber` is REQUIRED here and nowhere else in this file. `receipts.added` is
 * deserialized into the submit's `ReceivingLogReceipt` model, whose `PoNumber` is a
 * non-nullable `string`, so ASP.NET rejects the request without it — regardless of
 * the backend then stamping the log's own PO over the value. See the §4.2 correction
 * in this file's header.
 *
 * It is a distinct type rather than an optional field on {@link ReceiptSubmitRow}
 * because the two paths genuinely differ: on `POST /V1/receiving-log` the PO comes
 * from the request body and a row carrying its own would be noise, while here there
 * is no PO anywhere else in the payload.
 */
export type ReceiptAddRow = ReceiptSubmitRow & { poNumber: string };

/**
 * A patch as the EDIT MAP determines it: the row's id plus only the fields the user
 * touched. Everything a sparse patch means, minus the one field that is not an edit.
 *
 * `buildReceiptUpdates` returns this rather than {@link ReceiptUpdatePatch} because it
 * is deliberately given no access to the loaded rows or the log — which is what makes
 * echoing a stored value structurally impossible there. The PO is stamped on one layer
 * up, by `buildLogEditRequest`, which knows which log is being edited.
 */
export type ReceiptFieldPatch = Omit<ReceiptUpdatePatch, 'poNumber'>;

export interface ReceiptChangesRequest {
  updated?: ReceiptUpdatePatch[];
  added?: ReceiptAddRow[];
  /** Refused with a `400` when a row has damage photos attached — remove the photo first. */
  deletedIds?: number[];
}

/**
 * Item-row (container) changes: which printed units to tombstone, why, and which
 * new units arrived.
 *
 * `added` carries `(quantity, itemType)` pairs only — see the `itemId` note in
 * this file's header (§3.6). A duplicate `itemType` is LEGAL here and merges
 * into that type's count ("one more box arrived"), unlike on submit — which is
 * also why adding one box renumbers every existing box label of that type
 * (§3.4): warn before saving, and read `renumberedItems` off the response.
 */
export interface ContainerChangesRequest {
  /** Item GUIDs to tombstone. Never deleted — each is printed in a QR on a physical box. */
  voidItemIds?: string[];
  /** Free text, stored verbatim; omit for `null`. Nothing is invented to fill it. */
  voidReason?: string;
  added?: ContainerItem[];
}

/**
 * Optimistic-concurrency assertion (§5). `MM.ReceivingLog` has no row version,
 * so this is the only thing standing between two open edit screens and the
 * second one silently reinstating numbering onto labels already on boxes.
 * Compared as SETS, so order is irrelevant.
 *
 * ⚠️ `activeItemIds` must come from the GET's own `activeItemIds` field — NEVER
 * from `details.items.map(...)`, which includes VOIDED (tombstoned) rows. Every
 * tombstone in the list reads to the backend as "an item you expected is no
 * longer active": `409` → reload → the same ids come back → `409` again,
 * forever. Any log that has ever had an item voided becomes permanently
 * unsavable, and no amount of reloading fixes it. The builder in
 * `receiving-log-save.rules.ts` takes a source type with no `items` member at
 * all so the wrong version cannot be written.
 */
export interface ExpectedRevision {
  activeItemIds: string[];
  receiptIds: number[];
}

/** Request body for `PUT /V1/receiving-log/{logId}` (handoff-18 §3). */
export interface UpdateReceivingLogRequest {
  /** Omitted entirely when no field was edited — an items-only or receipts-only edit is a normal `200` (§4.4). */
  logRow?: LogRowFieldEdits;

  /**
   * The note text, recomposed CLIENT-SIDE from the edited receipt rows with the
   * same `items-string.rules.ts` composition the submit uses (§2). The backend
   * cannot build it (it contains inferred missing-item text that is never
   * persisted) and does not try.
   *
   * OMIT to leave the stored text alone — right for an edit that changed no
   * receipts, wrong for one that did. Sending `""` writes an empty note, which
   * is a real edit and not the same as omitting.
   */
  items?: string;

  receipts?: ReceiptChangesRequest;
  containers?: ContainerChangesRequest;

  /**
   * Whole-PO completion. Omit to leave `MM.ReceivingLogPoStatus` untouched.
   * Keyed BY PO, not by log — setting it from one log's edit screen marks the
   * ENTIRE PO complete, which the UI has to say out loud. Refused on a log with
   * no PO (§4.3).
   */
  completesPo?: boolean;

  /** REQUIRED. Blank/missing is a `400`; the log row has no audit columns, so this is the only trace of who edited it. */
  editedBy: string;

  /** Optional on the wire, always sent from here — see {@link ExpectedRevision}. */
  expected: ExpectedRevision;
}
